/*
 * Tigerlino: Live-Vorschau des Motivs auf der Produktseite.
 * Zeichnet die Eingaben der Personalisierungsfelder auf die leere Vorlage.
 * Vorlagen: Shop-Metafeld tigerlino.vorschau_vorlagen, Zuordnung: Produkt-Metafeld tigerlino.vorschau_vorlage.
 * Positionen, Größen und Farben stammen aus den Photoshop-Dateien (siehe vorlagen/vorschau-vorlagen.json).
 */
class TigerlinoPreview extends HTMLElement {
  connectedCallback() {
    if (this.canvas) return;

    this.config = JSON.parse(this.querySelector('[data-tl-preview-config]').textContent);
    this.fonts = JSON.parse(this.querySelector('[data-tl-preview-fonts]').textContent);
    this.canvas = this.querySelector('[data-tl-preview-canvas]');
    this.canvas.width = this.config.width;
    this.canvas.height = this.config.height;

    const scope = this.closest('.tl-personalize') || document;
    const controls = [...scope.querySelectorAll('input, select, textarea')];
    this.inputs = new Map();
    for (const field of this.config.fields) {
      const input = controls.find((control) => control.name === `properties[${field.key}]`);
      if (!input) continue;
      this.inputs.set(field.key, input);
      input.addEventListener('input', () => this.requestDraw());
    }

    this.image = new Image();
    this.image.addEventListener('load', () => this.requestDraw());
    this.image.src = this.dataset.image;

    this.loadFonts().then(() => this.requestDraw());
    if (document.fonts) document.fonts.addEventListener('loadingdone', () => this.requestDraw());

    this.dialog = this.querySelector('dialog');
    this.querySelector('[data-tl-preview-open]')?.addEventListener('click', () => this.openZoom());
    this.dialog?.addEventListener('click', () => this.dialog.close());

    this.requestDraw();
  }

  loadFonts() {
    if (!document.fonts) return Promise.resolve();
    const loads = this.config.fields.map((field) => {
      const font = this.fontFor(field);
      try {
        return document.fonts.load(`${font.style} ${font.weight} 50px "${font.family}"`, field.ref.text).catch(() => {});
      } catch (error) {
        return Promise.resolve();
      }
    });
    return Promise.all(loads);
  }

  fontFor(field) {
    const font = this.fonts[field.font] || { family: 'serif', weight: '400', style: 'normal' };
    // Shopify liefert Familiennamen mit Leerzeichen bereits in Anführungszeichen ("Josefin Sans").
    return { ...font, family: String(font.family).replace(/^["']+|["']+$/g, '') };
  }

  requestDraw() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.draw(this.canvas);
    });
  }

  draw(canvas) {
    const ctx = canvas.getContext('2d');
    const { width, height } = this.config;
    ctx.clearRect(0, 0, width, height);
    if (this.image.complete && this.image.naturalWidth) ctx.drawImage(this.image, 0, 0, width, height);
    for (const field of this.config.fields) {
      const value = (this.inputs.get(field.key)?.value || '').trim();
      this.drawField(ctx, field, value || field.placeholder || '', !value);
    }
  }

  drawField(ctx, field, rawText, isPlaceholder) {
    if (!rawText) return;
    const font = this.fontFor(field);
    const upper = (text) => (field.uppercase ? text.toLocaleUpperCase('de-DE') : text);
    const text = upper(rawText);
    const sample = upper(field.ref.text);
    const [left, top, right, bottom] = field.ref.box;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    const setSize = (size) => {
      ctx.font = `${font.style} ${font.weight} ${size}px "${font.family}"`;
      if ('letterSpacing' in ctx) ctx.letterSpacing = `${((field.tracking || 0) / 1000) * size}px`;
    };

    // Größe so wählen, dass das Musterwort so groß wirkt wie in der Photoshop-Datei (gleicht Ersatzschriften an).
    let size = field.size;
    setSize(size);
    let sampleMetrics = ctx.measureText(sample);
    const inkWidth = sampleMetrics.actualBoundingBoxLeft + sampleMetrics.actualBoundingBoxRight;
    const inkHeight = sampleMetrics.actualBoundingBoxAscent + sampleMetrics.actualBoundingBoxDescent;
    if (inkWidth > 0 && inkHeight > 0) {
      size *= Math.sqrt(((right - left) / inkWidth) * ((bottom - top) / inkHeight));
      setSize(size);
      sampleMetrics = ctx.measureText(sample);
    }
    let baseline = bottom - sampleMetrics.actualBoundingBoxDescent;
    const x = (left + right) / 2;

    // Zu lange Eingaben verkleinern, bis sie in den vorgesehenen Bereich passen.
    const textWidth = ctx.measureText(text).width;
    if (textWidth > field.maxWidth) {
      const ascentBefore = sampleMetrics.actualBoundingBoxAscent;
      size *= field.maxWidth / textWidth;
      setSize(size);
      if (field.valign === 'middle') {
        baseline -= (ascentBefore - ctx.measureText(sample).actualBoundingBoxAscent) / 2;
      }
    }

    ctx.globalAlpha = isPlaceholder ? 0.4 : 1;
    if (field.glow) {
      ctx.shadowColor = field.glow.color;
      ctx.shadowBlur = field.glow.blur;
    }
    if (field.stroke) {
      ctx.lineJoin = 'round';
      ctx.lineWidth = field.stroke.width * 2;
      ctx.strokeStyle = field.stroke.color;
      ctx.strokeText(text, x, baseline);
      ctx.shadowColor = 'transparent';
    }
    if (Array.isArray(field.color)) {
      const metrics = ctx.measureText(text);
      const gradient = ctx.createLinearGradient(
        0,
        baseline - metrics.actualBoundingBoxAscent,
        0,
        baseline + metrics.actualBoundingBoxDescent
      );
      field.color.forEach((color, index) => gradient.addColorStop(index / (field.color.length - 1), color));
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = field.color;
    }
    ctx.fillText(text, x, baseline);
    ctx.restore();
  }

  openZoom() {
    if (!this.dialog) return;
    const zoomCanvas = this.dialog.querySelector('canvas');
    zoomCanvas.width = this.config.width;
    zoomCanvas.height = this.config.height;
    this.draw(zoomCanvas);
    this.dialog.showModal();
  }
}

if (!customElements.get('tl-preview')) customElements.define('tl-preview', TigerlinoPreview);
